// react
import React, { useState, useMemo } from "react";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import { Box, Table, Typography, TableBody, TableCell, TableHead, TableRow, TableContainer, TableFooter, TablePagination, IconButton } from "@mui/material";
import { KeyboardArrowLeftOutlined as KeyboardArrowLeftOutlinedIcon, KeyboardArrowRightOutlined as KeyboardArrowRightOutlinedIcon } from "@mui/icons-material";

// utils
import formatDate from "utils/formatDate";
import { getStatusChip } from "./utils/statusVisuals";

// A ---------------------------------------------------------------------- M

const SourcesTableHead = () => {
  const { t } = useTranslation();
  const headCells = useMemo(
    () => [
      { id: "name", label: t("name") },
      { id: "status", label: t("status") },
      { id: "last_datapoint", label: t("last_datapoint") },
      { id: "last_update", label: t("last_update") },
    ],
    [t]
  );

  return (
    <TableHead sx={{ backgroundColor: "grey.100" }}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align="left">
            <Typography variant="body2" fontWeight="bold">
              {headCell.label}
            </Typography>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }) => {
  const handleBack = (event) => onPageChange(event, page - 1);
  const handleNext = (event) => onPageChange(event, page + 1);

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleBack} disabled={page === 0} aria-label="previous page">
        <KeyboardArrowLeftOutlinedIcon />
      </IconButton>
      <IconButton onClick={handleNext} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
        <KeyboardArrowRightOutlinedIcon />
      </IconButton>
    </Box>
  );
};

const AuthorizedKeysStatusTable = ({ sourcesData }) => {
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - sourcesData.length) : 0;

  return (
    <TableContainer>
      <Table>
        <SourcesTableHead />
        <TableBody>
          {sourcesData.length > 0 ? (
            (rowsPerPage > 0 ? sourcesData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : sourcesData).map((source) => (
              <TableRow key={source.keyId} hover>
                <TableCell align="left">{source.name}</TableCell>
                <TableCell align="left">{getStatusChip(source.status)}</TableCell>
                <TableCell align="left">{source.lastEmittedPacket ? formatDate(source.lastEmittedPacket) : "N/A"}</TableCell>
                <TableCell align="left">{source.lastUpdated ? formatDate(source.lastUpdated) : "N/A"}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} align="center">
                {t("no_source_found")}
              </TableCell>
            </TableRow>
          )}
          {emptyRows > 0 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <TableCell colSpan={4} />
            </TableRow>
          )}
        </TableBody>
        <TableFooter sx={{ backgroundColor: "grey.100" }}>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[10, 20]}
              colSpan={4}
              count={sourcesData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={t("rows_per_page")}
              labelDisplayedRows={({ from, to, count }) => t("displayed_rows", { from, to, count: count !== -1 ? count : `more than ${to}` })}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

export default AuthorizedKeysStatusTable;
